import { ChangeDetectionStrategy, Component, computed } from '@angular/core';
import { ReadonlyDisplayBase } from './readonly-display.base';

/**
 * Read display for the rich-text field (#21239): the stored HTML rendered as the author wrote
 * it, instead of a line of escaped tags.
 *
 * `[innerHTML]` goes through Angular's own sanitizer (SecurityContext.HTML) — deliberately NOT
 * `bypassSecurityTrustHtml`. The value is entry data: any user who can fill in the field would
 * otherwise decide what runs in the reader's session. TinyMCE's own output survives sanitising
 * (paragraphs, lists, emphasis, links, tables); a script does not. Inline `style` attributes do
 * NOT survive it either — Angular's sanitizer drops the `style` attribute outright (task 21245),
 * so read mode shows the author's STRUCTURE (headings, lists, emphasis, links, tables) rather
 * than hand-picked colors, fonts or alignment from the editor's toolbar; it is a read view, not
 * a WYSIWYG replay.
 *
 * The row already treats `editor` as multiline (`MULTILINE_TYPES` in
 * `readonly-field-row.component.ts`), so this display can wrap over as many lines as the answer
 * needs.
 *
 * Presentational: both inputs come from the row.
 */
@Component({
  selector: 'dd-html-display',
  changeDetection: ChangeDetectionStrategy.OnPush,
  styles: `
    :host {
      display: block;
    }

    /* ::ng-deep is required, not sloppiness: the markup is inserted at RUNTIME and therefore
       never carries this component's emulated-encapsulation attribute, so a scoped selector
       cannot match it. Scoped under :host and .dd-html-display so the rules cannot reach
       anything outside this display. Type and color are inherited from the row's value span —
       only the block rhythm of prose is restored here. */
    :host ::ng-deep .dd-html-display {
      p,
      ul,
      ol,
      blockquote,
      table {
        margin: 0 0 0.5em;
      }

      > *:last-child {
        margin-bottom: 0;
      }

      ul,
      ol {
        padding-left: 1.25em;
        list-style-position: outside;
      }

      ul {
        list-style-type: disc;
      }

      ol {
        list-style-type: decimal;
      }

      a {
        color: inherit;
        border-bottom: 1px solid currentColor;
        text-decoration: none;
      }

      img {
        max-width: 100%;
        height: auto;
      }
    }
  `,
  template: `
    @if (html(); as html) {
      <div class="dd-html-display" [innerHTML]="html"></div>
    } @else {
      —
    }
  `,
})
export class HtmlDisplayComponent extends ReadonlyDisplayBase {
  /** The markup, or `null` for an answer with nothing to read — which becomes the row's em dash. */
  readonly html = computed(() => {
    const raw = this.value();
    return typeof raw === 'string' && hasVisibleContent(raw) ? raw : null;
  });
}

/**
 * True when the markup renders anything. TinyMCE leaves `<p></p>` (and `<p>&nbsp;</p>`) behind
 * when a user clears the editor: as an answer that is empty, and an empty paragraph in a read
 * row reads as a rendering gap rather than as "no answer". Markup without text can still be
 * worth showing when it embeds something — an image, a rule, a table.
 *
 * `iframe` is deliberately NOT in that embed list (task 21245): Angular's sanitizer strips
 * `<iframe>` outright — it is never on the `SecurityContext.HTML` allow-list — so a stored
 * iframe never reaches the DOM here regardless of this check. Counting it as "visible" would
 * make an answer that is really just a silently-stripped iframe read as filled in.
 */
function hasVisibleContent(html: string): boolean {
  const text = html
    .replace(/<[^>]*>/g, '')
    .replace(/&nbsp;/gi, ' ')
    .trim();
  return text !== '' || /<(img|video|audio|hr|table)\b/i.test(html);
}
